<script lang="ts">
  // BJL-CHECK: InputBare (used), Button (used), Badge (used, counts), Tag (used, borough
  // chips), Input, Tabs, Filter, Card. `Tag` was rejected for the borough chips because
  // catppuccin's Tag.css ships colours but no layout, so it renders as bare text. The route
  // cards stay real `<button aria-pressed>` toggles, since Card hardcodes
  // `aria-pressed="false"` and would tell a screen reader the line is never selected. The
  // bullets are MTA roundels painted from route data. See artifacts/deviations/SearchPage.md.
  import { InputBare } from '@byjackli-labs/ui/Input'
  import { Button } from '@byjackli-labs/ui/Button'
  import { Badge } from '@byjackli-labs/ui/Badge'
  import { Colors, getRouteColor, busBorough, BUS_BOROUGH_ORDER } from '../ui/colors'
  import { bulletFontPx, bulletText, Type } from '../ui/theme'
  import { routeVehicleCounts, useTransitStore } from '../store/transit'
  import { useFavoritesStore } from '../store/favorites'
  import { api, type BusRoute } from '../services/api'
  import { storeSelect } from '../lib/store.svelte'
  import RouteBadge from '../components/RouteBadge.svelte'

  const ALL_ROUTES = [
    '1', '2', '3', '4', '5', '6', '7',
    'A', 'C', 'E', 'B', 'D', 'F', 'M', 'G', 'J', 'Z', 'L',
    'N', 'Q', 'R', 'W', 'SI',
  ]

  const ROUTE_NAMES: Record<string, string> = {
    '1': 'Broadway – 7 Av Local',
    '2': 'Seventh Avenue Express',
    '3': 'Seventh Avenue Express',
    '4': 'Lexington Avenue Express',
    '5': 'Lexington Avenue Express',
    '6': 'Lexington Avenue Local',
    '7': 'Flushing Local/Express',
    'A': 'Eighth Avenue Express',
    'C': 'Eighth Avenue Local',
    'E': 'Queens Boulevard Local',
    'B': 'Sixth Avenue Express',
    'D': 'Sixth Avenue Express',
    'F': 'Queens Blvd/6th Ave Local',
    'M': 'Queens Blvd/6th Ave Local',
    'G': 'Crosstown Local',
    'J': 'Nassau St Local',
    'Z': 'Nassau St Express',
    'L': 'Canarsie Local',
    'N': 'Broadway Express',
    'Q': 'Broadway Express',
    'R': 'Broadway Local',
    'W': 'Broadway Local',
    'SI': 'Staten Island Railway',
  }

  const vehicleCounts = storeSelect(useTransitStore, routeVehicleCounts)
  const alerts = storeSelect(useTransitStore, (s) => s.alerts)
  const selectedRouteIds = storeSelect(useTransitStore, (s) => s.selectedRouteIds)
  const favoriteRoutes = storeSelect(useFavoritesStore, (s) => s.favoriteRoutes)
  const favoriteBusRoutes = storeSelect(useFavoritesStore, (s) => s.favoriteBusRoutes)

  let query = $state('')
  // All NYC bus lines, loaded once and grouped by borough below.
  let busRoutes = $state<BusRoute[] | null>(null)
  let busError = $state(false)
  // Separate tabs, so the ~300 bus lines don't sit behind the whole subway grid.
  let tab = $state<'subway' | 'bus'>('subway')
  // Active borough filter for the bus tab ('All' = every borough, grouped).
  let boroughFilter = $state('All')

  $effect(() => {
    let alive = true
    api
      .getBusRoutes()
      .then((d) => { if (alive) busRoutes = d.routes })
      .catch(() => { if (alive) busError = true })
    return () => { alive = false }
  })

  const searching = $derived(query.trim().length > 0)

  // Matches the line designator anywhere in it: "4" finds the 4 train and the M42. Long
  // names are excluded: they're streets and destinations, so including them turned a search
  // for a line into a search for a corridor, and "4" returned every bus running on 4th Av.
  // A rider searching here already knows which line they want.
  const filteredSubway = $derived.by(() => {
    const q = query.trim().toUpperCase()
    if (!q) return ALL_ROUTES
    return ALL_ROUTES.filter((r) => r.includes(q))
  })

  // Grouped by borough in a fixed order, so sections read consistently; empty groups drop.
  const busGroups = $derived.by(() => {
    if (!busRoutes) return []
    const q = query.trim().toUpperCase()
    const groups = new Map<string, BusRoute[]>()
    for (const route of busRoutes) {
      if (q && !route.id.toUpperCase().includes(q)) continue
      const borough = busBorough(route.id)
      const list = groups.get(borough) ?? []
      list.push(route)
      groups.set(borough, list)
    }
    // Prefix matches lead: "15" means the M15 far more often than the BxM15, which a plain
    // substring match would bury under every express line.
    if (q) {
      for (const list of groups.values()) {
        list.sort(
          (a, b) =>
            Number(!a.id.toUpperCase().startsWith(q)) - Number(!b.id.toUpperCase().startsWith(q)),
        )
      }
    }
    return BUS_BOROUGH_ORDER.map((borough) => ({
      borough: borough as string,
      routes: groups.get(borough) ?? [],
    })).filter((g) => g.routes.length > 0)
  })

  // Drives the tab's count badge and the empty states.
  const busCount = $derived(busGroups.reduce((n, g) => n + g.routes.length, 0))

  // Every group under 'All', otherwise just the picked borough. Search ignores this filter
  // (results below are unified), so it only drives the browse view.
  const shownBusGroups = $derived(
    boroughFilter === 'All' ? busGroups : busGroups.filter((g) => g.borough === boroughFilter),
  )

  // Every fleet's counts, not just the fleets this client receives (routeVehicleCounts).
  const vehicleCountByRoute = $derived(vehicleCounts.value)

  // In-effect only: counting the month of planned weekend work MTA also publishes would pin
  // a permanent "12⚠" on nearly every line, which says nothing.
  const alertCountByRoute = $derived.by(() => {
    const counts: Record<string, number> = {}
    for (const alert of alerts.value) {
      if (alert.upcoming) continue
      for (const r of alert.routeIds) {
        counts[r] = (counts[r] ?? 0) + 1
      }
    }
    return counts
  })

  const boroughChips = $derived([
    { borough: 'All', count: busCount },
    ...busGroups.map((g) => ({ borough: g.borough, count: g.routes.length })),
  ])
</script>

{#snippet sectionLabel(text: string)}
  <div
    style="color: {Colors.textMuted}; {Type.eyebrow}; padding: 14px 0 8px;"
  >{text}</div>
{/snippet}

{#snippet favoriteStar(favorited: boolean, onclick: () => void)}
  <!-- Favorite star: top-right corner, outside the select button (a nested <button> is
       invalid HTML). -->
  <Button
    variant="text"
    size="sm"
    iconOnly
    {onclick}
    aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
    aria-pressed={favorited}
    style="
      position: absolute; top: 2px; right: 2px; z-index: 1; font-size: 15px;
      color: {favorited ? Colors.warning : Colors.textSecondary};
    "
  >{favorited ? '★' : '☆'}</Button>
{/snippet}

<!-- Reusable subway route grid (used by the Subway tab and the unified search view).
     Rows grow to fit the longest route name in the row, so names are never clipped. -->
{#snippet subwayGrid(routeIds: string[])}
  <div
    style="
      display: grid; grid-template-columns: repeat(3, 1fr);
      grid-auto-rows: minmax(115px, auto); gap: 8px; align-content: start;
    "
  >
    {#each routeIds as routeId (routeId)}
      {@const color = getRouteColor(routeId)}
      {@const isSelected = selectedRouteIds.value.has(routeId)}
      {@const isFavorited = favoriteRoutes.value.has(routeId)}
      {@const vehicleCount = vehicleCountByRoute[routeId] ?? 0}
      {@const alertCount = alertCountByRoute[routeId] ?? 0}
      <div style="position: relative; height: 100%;">
        <button
          onclick={() => useTransitStore.getState().toggleSelectedRoute(routeId)}
          aria-pressed={isSelected}
          style="
            width: 100%; height: 100%;
            background-color: {isSelected ? `${color}18` : Colors.surface};
            border: 2px solid {isSelected ? color : Colors.border};
            border-radius: var(--bjl-radius-md, 12px); padding: 10px 8px;
            cursor: pointer; text-align: left;
            display: flex; flex-direction: column; gap: 6px;
          "
        >
          <!-- Route icon -->
          <RouteBadge {routeId} size={32} />

          <!-- Route name: wraps to as many lines as it needs rather than clipping -->
          <div
            style="
              color: {Colors.textSecondary}; {Type.caption};
              line-height: 1.35; overflow-wrap: anywhere;
            "
          >{ROUTE_NAMES[routeId] ?? routeId}</div>

          <!-- Vehicle / alert count: pinned to the bottom of the card -->
          <div
            style="
              margin-top: auto; padding-top: 4px;
              display: flex; align-items: center; flex-wrap: wrap; gap: 4px;
            "
          >
            <span style="color: {Colors.textMuted}; {Type.caption};">
              {vehicleCount > 0 ? `${vehicleCount} active` : 'No vehicles'}
            </span>
            {#if alertCount > 0}
              <Badge size="xs" variant="solid" tone="warning" shape="pill">{alertCount}⚠</Badge>
            {/if}
          </div>
        </button>

        {@render favoriteStar(isFavorited, () =>
          useFavoritesStore.getState().toggleFavoriteRoute(routeId),
        )}
      </div>
    {/each}
  </div>
{/snippet}

<!-- Reusable grid of bus cards for a single list of routes. minmax(0,1fr) columns so
     tracks can shrink below the cards' min-content width; bare 1fr floors at the
     longest unbreakable name token (e.g. "Downtown/Midtown"), which pushed the grid
     wider than the sheet and made the whole panel horizontally swipeable. Rows grow
     to fit each card's full route name (mirrors the subway grid).

     Tapping a card body selects the line for the map (only selected lines' buses are
     shown); the corner star favorites it. Selection is signalled by the route-colored
     border + tint, exactly as subway cards do. -->
{#snippet busGrid(routes: BusRoute[])}
  <div
    style="
      display: grid; grid-template-columns: repeat(3, minmax(0, 1fr));
      grid-auto-rows: minmax(96px, auto); gap: 8px;
    "
  >
    {#each routes as route (route.id)}
      {@const color = getRouteColor(route.id)}
      {@const selected = selectedRouteIds.value.has(route.id)}
      {@const favorited = favoriteBusRoutes.value.has(route.id)}
      <div style="position: relative; height: 100%;">
        <button
          onclick={() => useTransitStore.getState().toggleSelectedRoute(route.id)}
          aria-pressed={selected}
          aria-label={selected ? `Hide ${route.id} on the map` : `Show only ${route.id} on the map`}
          style="
            height: 100%; width: 100%;
            background-color: {selected ? `${color}18` : Colors.surface};
            border: 2px solid {selected ? color : Colors.border};
            border-radius: var(--bjl-radius-md, 12px); padding: 10px 8px;
            cursor: pointer; text-align: left;
            display: flex; flex-direction: column; gap: 7px; overflow: hidden;
          "
        >
          <!-- Route badge: bus ids are short strings (e.g. "M15", "Bx12", "B44+") -->
          <div
            style="
              min-width: 32px; height: 32px; padding: 0 6px; align-self: flex-start;
              background-color: {color}; border-radius: 8px; flex-shrink: 0;
              display: flex; align-items: center; justify-content: center;
            "
          >
            <span
              style="{bulletText(route.id, route.id.length > 4 ? 10 : 13)};"
            >{route.id}</span>
          </div>

          <!-- Route long name: right padding keeps it clear of the corner star. Wraps to as
               many lines as it needs (the row grows to fit) rather than clamping. Browsers
               don't treat "/" as a break opportunity, so a zero-width space after each slash
               lets "Downtown/Midtown" wrap as "Downtown/ Midtown": words stay whole, the
               card just gets taller. break-word stays as a fallback so a single word wider
               than the card (none known) clips inside it rather than widening the grid. -->
          <div
            style="
              flex: 1; color: {Colors.textSecondary}; {Type.caption};
              line-height: 1.3; padding: 0 12px 0 0; overflow-wrap: break-word;
            "
          >{(route.longName || route.id).replace(/\//g, '/​')}</div>
        </button>

        {@render favoriteStar(favorited, () =>
          useFavoritesStore.getState().toggleFavoriteBusRoute(route.id),
        )}
      </div>
    {/each}
  </div>
{/snippet}

{#snippet busNotice(text: string)}
  <div style="color: {Colors.textMuted}; {Type.body}; padding: 6px 0;">{text}</div>
{/snippet}

{#snippet groupedBusGrids(groups: { borough: string; routes: BusRoute[] }[])}
  <div style="display: flex; flex-direction: column; gap: 14px;">
    {#each groups as { borough, routes } (borough)}
      <div>
        {@render sectionLabel(`${borough} · ${routes.length}`)}
        {@render busGrid(routes)}
      </div>
    {/each}
  </div>
{/snippet}

<div
  style="
    flex: 1; min-height: 0; display: flex; flex-direction: column;
    background-color: {Colors.ground};
  "
>
  <!-- Search bar: a bjl InputBare carrying the theme's own field chrome, with a bjl Button
       to clear it. -->
  <div style="padding: 12px 16px 8px; display: flex; align-items: center; gap: 8px;">
    <InputBare
      type="search"
      bind:value={query}
      placeholder="Search by line — 4, M42, SIM…"
      aria-label="Search by line"
      style="flex: 1; min-width: 0;"
    />
    {#if query}
      <Button
        variant="text"
        size="sm"
        iconOnly
        onclick={() => { query = '' }}
        aria-label="Clear search"
      >✕</Button>
    {/if}
  </div>

  <!-- Selected-routes summary + clear button -->
  {#if selectedRouteIds.value.size > 0}
    <div
      style="padding: 4px 16px 10px; display: flex; align-items: center; justify-content: space-between;"
    >
      <span style="color: {Colors.textSecondary}; {Type.body};">
        {selectedRouteIds.value.size} route{selectedRouteIds.value.size !== 1 ? 's' : ''} selected
      </span>
      <Button
        variant="secondary"
        size="sm"
        onclick={() => useTransitStore.getState().clearSelectedRoutes()}
      >Clear all</Button>
    </div>
  {/if}

  <!-- Subway / Bus tabs, hidden while searching, since search spans both modes.
       Buses live behind their own tab so they're one tap away instead of a long
       scroll past the whole subway grid. -->
  {#if !searching}
    <div style="display: flex; gap: 6px; padding: 2px 16px 10px;">
      {#each [{ key: 'subway' as const, label: 'Subway', count: filteredSubway.length }, { key: 'bus' as const, label: 'Bus', count: busRoutes ? busCount : null }] as { key, label, count } (key)}
        {@const active = tab === key}
        <Button
          variant={active ? 'default' : 'secondary'}
          onclick={() => { tab = key }}
          aria-pressed={active}
          style="flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px;"
        >
          {label}
          {#if count != null}
            <!-- Explicit colors: Badge's default tone is --bjl-color-primary, which on the
                 ACTIVE button (a solid primary fill) would be blue-on-blue and vanish. -->
            <Badge
              size="xs"
              register="mono"
              colors={active
                ? { bg: Colors.background, text: Colors.primary }
                : { bg: Colors.surfaceElevated, text: Colors.textSecondary }}
            >{count}</Badge>
          {/if}
        </Button>
      {/each}
    </div>
  {/if}

  <!-- overflow-x hidden: with overflow-y auto alone, any child wider than the sheet
       makes the whole panel horizontally swipeable (whitespace on the right). -->
  <div style="flex: 1; overflow-y: auto; overflow-x: hidden; padding: 0 16px 24px;">
    {#if searching}
      <!-- Unified search results (both modes) -->
      {#if filteredSubway.length > 0}
        {@render sectionLabel('Subway')}
        {@render subwayGrid(filteredSubway)}
      {/if}

      {@render sectionLabel('Bus')}
      {#if busError}
        {@render busNotice('Couldn’t load bus routes. Pull to refresh or try again later.')}
      {:else if !busRoutes}
        {@render busNotice('Loading bus lines…')}
      {:else if busGroups.length === 0}
        {@render busNotice('No bus lines match your search.')}
      {:else}
        {@render groupedBusGrids(busGroups)}
      {/if}

      {#if filteredSubway.length === 0 && busGroups.length === 0 && !busError && busRoutes}
        <div style="color: {Colors.textMuted}; {Type.body}; padding: 12px 0;">
          No routes match “{query.trim()}”.
        </div>
      {/if}
    {:else if tab === 'subway'}
      {@render subwayGrid(filteredSubway)}
    {:else if busError}
      {@render busNotice('Couldn’t load bus routes. Pull to refresh or try again later.')}
    {:else if !busRoutes}
      {@render busNotice('Loading bus lines…')}
    {:else if busGroups.length === 0}
      {@render busNotice('No bus lines available.')}
    {:else}
      <!-- Borough filter chips: horizontally scrollable so riders can jump
           straight to their borough instead of scrolling every group. -->
      <div
        style="
          display: flex; gap: 8px; overflow-x: auto; padding: 4px 0 12px;
          -webkit-overflow-scrolling: touch; scrollbar-width: none;
        "
      >
        {#each boroughChips as { borough, count } (borough)}
          {@const active = boroughFilter === borough}
          <Button
            variant={active ? 'default' : 'secondary'}
            size="sm"
            aria-pressed={active}
            onclick={() => { boroughFilter = borough }}
            style="flex-shrink: 0; white-space: nowrap;"
          >
            {borough} · {count}
          </Button>
        {/each}
      </div>

      {#if shownBusGroups.length === 0}
        {@render busNotice(`No bus lines in ${boroughFilter}.`)}
      {:else if boroughFilter === 'All'}
        {@render groupedBusGrids(shownBusGroups)}
      {:else}
        {@render busGrid(shownBusGroups[0].routes)}
      {/if}
    {/if}
  </div>
</div>
